import { useNavigate } from 'react-router-dom';
import type { MessageKey } from '@bookingdei/i18n/message-key';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import { formatNumber } from '@bookingdei/i18n/format-number';
import { useLanguage } from '../../shell/language-context';
import { NavIcon } from '../../shell/nav-icon';
import type { Business, OnboardingChecklist } from '../../mock/seeds/schema-types';
import { MISSING_KEY } from '../business/listing-extras-model';

type Step = OnboardingChecklist['steps'][number];

// Where each step's Start button goes (design go(['business','services','services','business'])).
const STEP_ROUTE: Record<string, string> = {
  create_business: '/business',
  add_service: '/services',
  share_link: '/services',
  turn_on_payments: '/business',
};
const STEP_KEY: Record<string, MessageKey> = {
  create_business: 'dc71.checklist.step.create_business',
  add_service: 'dc71.checklist.step.add_service',
  share_link: 'dc71.checklist.step.share_link',
  turn_on_payments: 'dc71.checklist.step.turn_on_payments',
};

// "Get set up" (M:387-415; GET /merchant/onboarding, API-104): numbered steps, a done
// tick, Start, or — when the plan does not include the step (`locked_by_plan`) — the
// name of the lowest plan that does. Shown until every step is done; Dismiss hides it
// for this browser (no contract field records a dismissal).
// DR-87: the listing with the lowest "Details on file" score — what to add, and a
// Complete button into its profile. Rendered under the checklist, or on its own once
// the checklist is done (home-screen.tsx).
export function HomeDetailsLine({ business }: { business: Business }) {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const details = business.details;
  if (!details) return null;
  const name = lang === 'bn' && business.name_bn ? business.name_bn : business.name;
  const missing = details.missing.map((m) => t(MISSING_KEY[m])).join(', ');
  return (
    <div className="hm-details" data-testid="details-line">
      <div className="hm-details__text">
        <span>{formatMessage(lang, 'lr.merchant.details.line', { name, n: details.score })}</span>
        {missing && <span className="hm-details__missing">{formatMessage(lang, 'lr.merchant.details.missing', { list: missing })}</span>}
      </div>
      <button type="button" className="hm-btn hm-btn--sm" onClick={() => navigate(`/business?b=${encodeURIComponent(business.id)}&tab=profile`)}>
        {t('lr.merchant.details.go')}
      </button>
    </div>
  );
}

export function HomeChecklist({
  steps,
  lockedPlanName,
  onDismiss,
  incomplete,
}: {
  steps: readonly Step[];
  lockedPlanName: string;
  onDismiss: () => void;
  /** The least complete listing (DR-87), when one is not at 5 of 5. */
  incomplete?: Business | null;
}) {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const done = steps.filter((s) => s.done).length;

  return (
    <section className="hm-card" aria-labelledby="hm-checklist-title">
      <div className="hm-card__head">
        <h3 id="hm-checklist-title" className="hm-card__title">
          {t('dc71.checklist.title')}
        </h3>
        <span className="hm-count">
          {formatMessage(lang, 'dc71.checklist.progress', { n: done, total: steps.length })}
        </span>
        <button type="button" className="hm-link hm-push" onClick={onDismiss}>
          {t('dc71.checklist.dismiss')}
        </button>
      </div>
      <ol className="hm-checklist">
        {steps.map((s, i) => {
          const key = STEP_KEY[s.key];
          const locked = !s.done && Boolean(s.locked_by_plan);
          return (
            <li key={s.key} className="hm-checklist__row">
              <span className="hm-checklist__n">{formatNumber(lang, i + 1)}</span>
              <span className="hm-checklist__label">{key ? t(key) : s.key}</span>
              {s.done ? (
                <span className="hm-checklist__done" role="img" aria-label={t('dc71.checklist.done')}>
                  <NavIcon name="check" size={14} strokeWidth={2.2} />
                </span>
              ) : locked ? (
                <span className="hm-checklist__lock">{lockedPlanName}</span>
              ) : (
                <button type="button" className="hm-btn hm-btn--sm" onClick={() => navigate(STEP_ROUTE[s.key] ?? '/')}>
                  {t('dc71.checklist.start')}
                </button>
              )}
            </li>
          );
        })}
      </ol>
      {incomplete && <HomeDetailsLine business={incomplete} />}
    </section>
  );
}
